import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ColumnConfig, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import InfoIndicator from "@app/shared/InfoIndicator";
import { JobPerformanceActiveJob, JobPerformanceDashboardRequest } from "@app/operations/job-performance/jobPerformanceInterfaces";
import { JobPerformanceService } from "@app/operations/job-performance/jobPerformanceService";

const PAGE_SIZE = 10;

const STATUS_TOOLTIP = 'Running: the job has started and has not finished. Queued: the job has not started yet. Failed — Retry Pending: the job failed and a retry has already been scheduled. Failed — Action Required: the job failed and no retry has been scheduled, so it needs manual attention.';
const FAILED_STATE_TOOLTIP = 'How long this job has been in a failed state, counted from the moment it failed. Keeps counting up until the job is retried or resolved — scheduling a retry does not stop the clock.';
const RETRIED_OUTSIDE_RANGE_TOOLTIP = "This job's original queue date is outside your selected date range — it's showing because it's actively being retried within your selected range.";

const StatusHeader = (
    <span style={{ alignItems: 'center', display: 'inline-flex', gap: '3px' }}>
        Status
        <InfoIndicator className="jp-section-header__icon" tooltipTitle={STATUS_TOOLTIP} tooltipWidth="320px" />
    </span>
);

const FailedStateHeader = (
    <span style={{ alignItems: 'center', display: 'inline-flex', gap: '3px' }}>
        Failed State (Min)
        <InfoIndicator className="jp-section-header__icon" tooltipTitle={FAILED_STATE_TOOLTIP} tooltipWidth="320px" />
    </span>
);

type ActiveJobRow = {
    [wafflesKeyColumnName]: string;
    client:             string;
    failedState:        string;
    jobId:              number;
    jobType:            string;
    projectId:          number | null;
    projectIdFormatted: React.ReactNode;
    projectName:        string | null;
    queue:              string;
    queueWait:          string;
    runTime:            string;
    status:             React.ReactNode;
    statusOrder:        number;
    statusUpdatedAt:    string;
    statusUpdatedAtRaw: string;
};

type Props = {
    filters: JobPerformanceDashboardRequest;
    refreshToken: number;
};

const statusChip = (s: 'FailedActionRequired' | 'FailedRetryPending' | 'Queued' | 'Running', isRetriedOutsideDateRange: boolean): React.ReactNode => {
    const chip =
        s === 'Running'              ? <span className="jp-chip jp-chip--running">Running</span> :
        s === 'FailedRetryPending'   ? <span className="jp-chip jp-chip--warning">Failed — Retry Pending</span> :
        s === 'FailedActionRequired' ? <span className="jp-chip jp-chip--danger">Failed — Action Required</span> :
        <span className="jp-chip jp-chip--muted">Queued</span>;

    if (!isRetriedOutsideDateRange) return chip;

    return (
        <span style={{ alignItems: 'center', display: 'inline-flex', gap: '3px' }}>
            {chip}
            <InfoIndicator className="jp-section-header__icon" tooltipTitle={RETRIED_OUTSIDE_RANGE_TOOLTIP} tooltipWidth="320px" />
        </span>
    );
};

const statusOrder = (s: JobPerformanceActiveJob['status']): number => {
    if (s === 'FailedActionRequired') return 3;
    if (s === 'FailedRetryPending')   return 2;
    if (s === 'Running')              return 1;
    return 0;
};

const toRow = (r: JobPerformanceActiveJob): ActiveJobRow => ({
    client:             r.clientName,
    failedState:        r.failedStateMinutes?.toFixed(1) ?? '—',
    jobId:              r.jobId,
    jobType:            r.jobTypeName,
    projectId:          r.projectId ?? null,
    projectIdFormatted: r.projectRunConfigurationId != null
                            ? <>{r.projectName ?? ''}{r.projectName ? ' ' : ''}({r.projectId ?? '—'} - {r.projectRunConfigurationId})</>
                            : '—',
    projectName:        r.projectName ?? null,
    queue:              r.jobQueueName,
    queueWait:          r.queueWaitMinutes?.toFixed(1) ?? '—',
    runTime:            r.runTimeMinutes?.toFixed(1) ?? '—',
    status:             statusChip(r.status, r.isRetriedOutsideDateRange),
    statusOrder:        statusOrder(r.status),
    statusUpdatedAt:    new Date(r.statusUpdatedAt).toLocaleString(),
    statusUpdatedAtRaw: String(r.statusUpdatedAt),
    [wafflesKeyColumnName]: r.jobId.toString(),
});

export const JobPerformanceActiveJobsTable: FC<Props> = ({ filters, refreshToken }) => {
    const service = useRef(new JobPerformanceService()).current;

    const [items, setItems]             = useState<JobPerformanceActiveJob[]>([]);
    const [totalCount, setTotalCount]   = useState(0);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setItems([]);
        setTotalCount(0);
        service.getActiveJobs({ ...filters, offset: 0, pageSize: PAGE_SIZE })
            .then(result => {
                setItems(result.items);
                setTotalCount(result.totalCount);
            })
            .catch(() => setError('Failed to load active jobs. Please refresh and try again.'))
            .finally(() => setLoading(false));
    }, [filters, refreshToken]);

    const tableData = useMemo<ActiveJobRow[]>(() => items.map(toRow), [items]);

    const loadMore = useCallback((justChecking: boolean): boolean => {
        if (justChecking) return items.length < totalCount;
        service.getActiveJobs({ ...filters, offset: items.length, pageSize: PAGE_SIZE })
            .then(result => {
                setItems(prev => [...prev, ...result.items]);
                setTotalCount(result.totalCount);
            })
            .catch(err => console.error('Failed to load more active jobs', err));
        return items.length + PAGE_SIZE < totalCount;
    }, [items.length, totalCount, filters.dateStart, filters.dateEnd]);

    const columnConfig: ColumnConfig = useMemo(() => ({
        client:             { displayText: 'Client',                                                         sortOrder: 10,  testId: 'active-jobs-client' },
        failedState:        { alignment: TextAlignment.Right, displayElement: FailedStateHeader, displayText: 'Failed State (Min)', sizeContentToFit: true, sortOrder: 60,  testId: 'active-jobs-failed-state' },
        jobId:              { alignment: TextAlignment.Right, displayText: 'Job ID',             sizeContentToFit: true, sortOrder: 80,  testId: 'active-jobs-job-id' },
        jobType:            { displayText: 'Job Type',                                                       sortOrder: 20,  testId: 'active-jobs-job-type' },
        projectId:          { displayText: 'Project ID',    hidden: true,                                                  testId: 'active-jobs-project-id' },
        projectIdFormatted: { displayText: 'Project',                                           sizeContentToFit: true, sortOrder: 100, testId: 'active-jobs-project-id-formatted' },
        projectName:        { displayText: 'Project Name',  hidden: true,                                                  testId: 'active-jobs-project-name' },
        queue:              { displayText: 'Job Queue',                                                       sortOrder: 30,  testId: 'active-jobs-queue' },
        queueWait:          { alignment: TextAlignment.Right, displayText: 'Queue Wait (Min)',    sizeContentToFit: true, sortOrder: 40,  testId: 'active-jobs-queue-wait' },
        runTime:            { alignment: TextAlignment.Right, displayText: 'Run Time (Min)',      sizeContentToFit: true, sortOrder: 50,  testId: 'active-jobs-run-time' },
        status:             { displayElement: StatusHeader, displayText: 'Status',                sizeContentToFit: true, sortOrder: 70,  testId: 'active-jobs-status' },
        statusOrder:        { displayText: 'Status Order', hidden: true,                                                  testId: 'active-jobs-status-order' },
        statusUpdatedAt:    { displayText: 'Status Updated',                                      sizeContentToFit: true, sortOrder: 35,  testId: 'active-jobs-status-updated' },
        statusUpdatedAtRaw: { displayText: 'Status Updated (raw)', hidden: true,                                                          testId: 'active-jobs-status-updated-raw' },
    }), []);

    // Waffles passes undefined as segment when loadMore is active (expects server-side sort).
    // We don't support server-side sort, so fall back to sorting whatever rows are loaded.
    const handleSort = useCallback((segment: ActiveJobRow[] | undefined, column: keyof ActiveJobRow, direction: SortDirections): ActiveJobRow[] => {
        const data = segment !== undefined ? segment : [...tableData];
        const mult = direction === SortDirections.Asc ? 1 : -1;
        if (column === 'failedState' || column === 'queueWait' || column === 'runTime') {
            return numberSort(data, column, mult);
        }
        if (column === 'status' || column === 'statusOrder') {
            return [...data].sort((a, b) => (a.statusOrder - b.statusOrder) * mult);
        }
        if (column === 'statusUpdatedAt' || column === 'statusUpdatedAtRaw') {
            return [...data].sort((a, b) => (String(a.statusUpdatedAtRaw) > String(b.statusUpdatedAtRaw) ? 1 : -1) * mult);
        }
        if (column === 'jobId') {
            return [...data].sort((a, b) => (a.jobId - b.jobId) * mult);
        }
        if (column === 'projectIdFormatted' || column === 'projectId') {
            return [...data].sort((a, b) => ((a.projectId ?? 0) - (b.projectId ?? 0)) * mult);
        }
        return [...data].sort((a, b) => (String(a[column] ?? '') > String(b[column] ?? '') ? 1 : -1) * mult);
    }, [tableData]);

    if (error) return <p className="jp-error-message">{error}</p>;

    return (
        <Waffles<ActiveJobRow>
            columns={columnConfig}
            customSort={handleSort}
            data={tableData}
            loadMore={loadMore}
            loadMoreLabel="See More"
            loading={loading}
            testId="job-performance__active-jobs-table"
        />
    );
};
